import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { IApprovalState } from './approval.state';
import { ApprovalPolicy, DepartmentApprovalPolicy } from '../../models/approval/approval-policy.model';

export const withApprovalStateMethods = signalStoreFeature(
    { state: type<IApprovalState>() },
    withMethods((store) => ({
        setPolicies: (policies: ApprovalPolicy[]) => {
            patchState(store, { policies, loading: false, error: null });
        },
        addPolicy: (policy: ApprovalPolicy) => {
            patchState(store, { policies: [policy, ...store.policies()], loading: false, error: null });
        },
        updatePolicy: (policy: ApprovalPolicy) => {
            patchState(store, {
                policies: store.policies().map((p) => (p.policyGuid === policy.policyGuid ? policy : p)),
                loading: false,
                error: null,
            });
        },
        removePolicy: (policyGuid: string) => {
            patchState(store, {
                policies: store.policies().filter((p) => p.policyGuid !== policyGuid),
                loading: false,
                error: null,
            });
        },
        setDepartmentPolicies: (items: any[]) => {
            const departmentPolicies: DepartmentApprovalPolicy[] = items.map((item) => ({
                departmentId:   item.departmentId,
                departmentGuid: item.departmentGuid,
                departmentName: item.departmentName,
                policies:       (item.policies ?? []).map((p: any) => ({
                    requestType: p.requestType,
                    policyId:    p.policyId   ?? null,
                    policyGuid:  p.policyGuid ?? null,
                    policyName:  p.policyName ?? null,
                })),
            }));
            patchState(store, { departmentPolicies, loading: false, error: null });
        },
        /** Update a single (department, requestType) slot in the store. */
        updateDepartmentPolicyEntry: (
            departmentGuid: string,
            requestType: string,
            policyGuid: string | null,
            policyName: string | null,
        ) => {
            patchState(store, {
                departmentPolicies: store.departmentPolicies().map((dp) => {
                    if (dp.departmentGuid !== departmentGuid) return dp;
                    const existing = dp.policies.find((p) => p.requestType === requestType);
                    const updated = policyGuid
                        ? { requestType, policyId: null, policyGuid, policyName }
                        : null; // null means: remove this entry (no assignment)
                    const otherPolicies = dp.policies.filter((p) => p.requestType !== requestType);
                    return {
                        ...dp,
                        policies: updated ? [...otherPolicies, updated] : otherPolicies,
                    };
                }),
                loading: false,
                error: null,
            });
        },
        setError: (error: string) => {
            patchState(store, { error, loading: false });
        },
    }))
);
